import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { API_BASE } from '../../config/api';
import { FaArrowRight, FaImage } from 'react-icons/fa';
import './AdminGalleryEdit.css';

const defaultGallery = {
  marquee_text:
    '* استشارات بحثية * توجيه أكاديمي * تمكين الباحثات * خبرة واقعية * دعم نفسي *',
  slide1_overlay_text: 'جلسات استشارية',
  slide2_overlay_text: 'دعم الباحثات',
  slide3_overlay_text: 'مصادر علمية',
  slide1_image_url: '',
  slide2_image_url: '',
  slide3_image_url:
    'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?q=80&w=1000&auto=format&fit=crop',
};

const AdminGalleryEdit = () => {
  const navigate = useNavigate();

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(defaultGallery);

  const [imageUrls, setImageUrls] = useState({
    slide1_image_url: '',
    slide2_image_url: '',
    slide3_image_url: '',
  });

  const [files, setFiles] = useState({
    image1: null,
    image2: null,
    image3: null,
  });

  const toAbsoluteImageUrl = (maybeUrl) => {
    if (!maybeUrl) return '';
    const u = String(maybeUrl).trim();
    if (!u) return '';
    if (u.startsWith('http://') || u.startsWith('https://')) return u;
    return `${API_BASE}${u}`;
  };

  useEffect(() => {
    axios
      .get(`${API_BASE}/api/admin/gallery`, { headers: getAuthHeaders() })
      .then((res) => {
        const g = res.data?.gallery;
        if (!g) return setForm(defaultGallery);

        setForm((prev) => ({
          ...prev,
          marquee_text: g.marquee_text ?? prev.marquee_text,
          slide1_overlay_text: g.slide1_overlay_text ?? prev.slide1_overlay_text,
          slide2_overlay_text: g.slide2_overlay_text ?? prev.slide2_overlay_text,
          slide3_overlay_text: g.slide3_overlay_text ?? prev.slide3_overlay_text,
        }));

        setImageUrls({
          slide1_image_url: g.slide1_image_url ?? '',
          slide2_image_url: g.slide2_image_url ?? '',
          slide3_image_url: g.slide3_image_url ?? '',
        });
      })
      .catch((err) => {
        console.error('Failed to load gallery:', err);
        setForm(defaultGallery);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleFile = (field, file) => {
    setFiles((prev) => ({ ...prev, [field]: file }));
  };

  const Preview = ({ url, label }) => {
    if (!url) return <div className="admin-gallery-preview-empty">{label}</div>;
    return (
      <div className="admin-gallery-preview">
        <img src={toAbsoluteImageUrl(url)} alt={label} />
      </div>
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('marquee_text', form.marquee_text);
      fd.append('slide1_overlay_text', form.slide1_overlay_text);
      fd.append('slide2_overlay_text', form.slide2_overlay_text);
      fd.append('slide3_overlay_text', form.slide3_overlay_text);

      if (files.image1) fd.append('image1', files.image1);
      if (files.image2) fd.append('image2', files.image2);
      if (files.image3) fd.append('image3', files.image3);

      await axios.put(`${API_BASE}/api/admin/gallery`, fd, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data',
        },
      });

      alert('تم حفظ قسم المعرض بنجاح');
      navigate('/admin');
    } catch (err) {
      alert(err.response?.data?.error || 'فشل حفظ قسم المعرض');
    } finally {
      setSaving(false);
    }
  };

  const header = useMemo(() => 'تعديل قسم المعرض', []);

  if (loading) return <div className="admin-loading">جاري التحميل...</div>;

  return (
    <div className="admin-gallery-edit" dir="rtl">
      <div className="admin-gallery-edit-header">
        <Link to="/admin" className="back-link">
          <FaArrowRight /> العودة للصفحة الرئيسية
        </Link>
        <h1 className="admin-page-title">{header}</h1>
      </div>

      <form onSubmit={handleSubmit} className="admin-gallery-form">
        <div className="form-row">
          <label>نص الماركي (Marquee)</label>
          <textarea
            className="form-input"
            value={form.marquee_text}
            onChange={(e) => handleChange('marquee_text', e.target.value)}
            rows={3}
            required
          />
        </div>

        <div className="admin-gallery-images-grid">
          <div className="admin-gallery-image-block">
            <div className="admin-gallery-image-label">الصورة 1</div>
            <Preview url={imageUrls.slide1_image_url} label="بدون صورة" />
            <div className="admin-gallery-upload">
              <FaImage />
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFile('image1', e.target.files?.[0] || null)}
              />
            </div>
            <div className="form-row">
              <label>نص الشريحة 1</label>
              <input
                type="text"
                className="form-input"
                value={form.slide1_overlay_text}
                onChange={(e) => handleChange('slide1_overlay_text', e.target.value)}
                required
              />
            </div>
          </div>

          <div className="admin-gallery-image-block">
            <div className="admin-gallery-image-label">الصورة 2</div>
            <Preview url={imageUrls.slide2_image_url} label="بدون صورة" />
            <div className="admin-gallery-upload">
              <FaImage />
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFile('image2', e.target.files?.[0] || null)}
              />
            </div>
            <div className="form-row">
              <label>نص الشريحة 2</label>
              <input
                type="text"
                className="form-input"
                value={form.slide2_overlay_text}
                onChange={(e) => handleChange('slide2_overlay_text', e.target.value)}
                required
              />
            </div>
          </div>

          <div className="admin-gallery-image-block">
            <div className="admin-gallery-image-label">الصورة 3</div>
            <Preview url={imageUrls.slide3_image_url} label="بدون صورة" />
            <div className="admin-gallery-upload">
              <FaImage />
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFile('image3', e.target.files?.[0] || null)}
              />
            </div>
            <div className="form-row">
              <label>نص الشريحة 3</label>
              <input
                type="text"
                className="form-input"
                value={form.slide3_overlay_text}
                onChange={(e) => handleChange('slide3_overlay_text', e.target.value)}
                required
              />
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="admin-btn-primary" disabled={saving}>
            {saving ? 'جارٍ الحفظ...' : 'حفظ'}
          </button>
          <button
            type="button"
            className="admin-btn-secondary"
            onClick={() => navigate('/admin')}
            disabled={saving}
          >
            إلغاء
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminGalleryEdit;

